// One tab retains only request/recovery identity and SHA-256 digests. Native
// contacts, card aliases, provider IDs and full forms never enter this storage.
const STORAGE_KEY = 'tph.continuity.monthly.v1';
const pendingMessage = 'Your monthly request is being checked. Do not submit another payment or card request.';
const canonical = value => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object'
  ? Object.fromEntries(Object.keys(value).sort().filter(key => value[key] !== undefined).map(key => [key, canonical(value[key])])) : value;
const safeInput = input => Object.fromEntries(Object.entries(input || {}).filter(([key]) => !['sourceId', 'idempotencyKey'].includes(key)));
const fail = (message = pendingMessage, status = 'pending', code) => Object.assign(new Error(message), { pending: true, status, code });
const endpoints = Object.freeze({ enroll: '/api/customer/monthly/enroll', 'replace-card': '/api/customer/monthly/payment-method', cancel: '/api/customer/monthly/cancel',
  'request-spot': '/api/customer/monthly/request-spot', 'reduce-spots': '/api/customer/monthly/reduce-spots' });
function errorMessage(code) {
  if (/SESSION_REQUIRED/.test(code || '')) return 'Please sign in again before continuing.';
  if (/ACTIVE_ASSIGNMENT_CONFLICT/.test(code || '')) return 'This vehicle or driver is assigned to monthly parking. Update the assignment before changing or removing it.';
  if (/COMMAND_UNSUPPORTED/.test(code || '')) return 'This monthly action is temporarily unavailable.';
  if (/PAYLOAD_CONFLICT/.test(code || '')) return 'Resolve the previous monthly request before changing it.';
  return pendingMessage;
}
export class MonthlyContinuityClient {
  constructor({ storage = window.sessionStorage, cryptoImpl = window.crypto, fetchImpl = window.fetch.bind(window),
    wait = () => new Promise(resolve => setTimeout(resolve, 1000)), maxPolls = 20 } = {}) {
    Object.assign(this, { storage, cryptoImpl, fetchImpl, wait, maxPolls });
  }
  async digest(value) {
    const data = await this.cryptoImpl.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(canonical(value))));
    return Array.from(new Uint8Array(data), n => n.toString(16).padStart(2, '0')).join('');
  }
  random() { return Array.from(this.cryptoImpl.getRandomValues(new Uint8Array(32)), n => n.toString(16).padStart(2, '0')).join(''); }
  save(value) { this.storage.setItem(STORAGE_KEY, JSON.stringify(value)); }
  async identity({ sessionToken, command, input, path, method }) {
    const ownerDigest = await this.digest(sessionToken), digest = await this.digest({ command, input: safeInput(input), path, method });
    let previous;
    try { previous = JSON.parse(this.storage.getItem(STORAGE_KEY) || 'null'); }
    catch (_) { throw fail('Monthly recovery data cannot be read. Contact support before submitting again.'); }
    if (previous && (!['requestId', 'recoveryToken', 'digest', 'ownerDigest'].every(key => /^[a-f0-9]{64}$/.test(previous[key] || ''))
      || ['submitted', 'completed'].some(key => typeof previous[key] !== 'boolean'))) throw fail('Monthly recovery data is invalid. Contact support.');
    if (previous && !previous.completed) {
      if (previous.ownerDigest !== ownerDigest || previous.digest !== digest) throw fail('Resolve the previous monthly request before changing or submitting another request in this tab.');
      return previous;
    }
    const value = { ownerDigest, digest, requestId: this.random(), recoveryToken: this.random(), submitted: false, completed: false };
    this.save(value); return value;
  }
  async request(sessionToken, path, body, { method = 'POST', headers = {} } = {}) {
    const response = await this.fetchImpl(path, { method, credentials: 'same-origin', cache: 'no-store', redirect: 'error', referrerPolicy: 'origin',
      headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'application/json', ...headers },
      ...(method === 'GET' ? {} : { body: JSON.stringify(body || {}) }) });
    const data = await response.json();
    if (!response.ok || data.success === false) throw Object.assign(fail(errorMessage(data.error), data.status, data.error), { httpStatus: response.status });
    return data;
  }
  async execute({ sessionToken, command, input = {}, tokenize, path = endpoints[command], method = 'POST', profile = false, onPending = () => {} }) {
    if (this.busy) throw fail();
    this.busy = true;
    try {
      if (!profile && !endpoints[command]) throw fail('This monthly action is not converted.', 'rejected', 'CONTINUITY_MONTHLY_COMMAND_UNSUPPORTED');
      if (typeof sessionToken !== 'string' || !sessionToken || !path?.startsWith('/api/customer/')) throw fail('Sign in before submitting a monthly request.');
      const attempt = await this.identity({ sessionToken, command, input, path, method });
      let state;
      if (attempt.submitted) state = await this.request(sessionToken, profile ? '/api/customer/profile/recover' : '/api/customer/monthly/recover', {
        requestId: attempt.requestId, ...(profile ? {} : { recoveryToken: attempt.recoveryToken }) });
      else {
        const body = { ...input };
        // Identity is durable before tokenization. Submitted is durable before
        // admission. A lost HTTP ACK/remount never calls tokenize again.
        if (tokenize) { const result = await tokenize(); if (!result?.token) throw fail('Card tokenization failed.'); body.sourceId = result.token; }
        attempt.submitted = true; this.save(attempt);
        state = await this.request(sessionToken, path, body, { method, headers: {
          'X-Idempotency-Key': attempt.requestId, 'X-Monthly-Recovery': attempt.recoveryToken } });
      }
      for (let n = 0; n < this.maxPolls; n++) {
        if (state.status === 'completed') {
          if (!state.profile?.id || (!profile && !/^monthly_[a-f0-9]{64}$/.test(state.intentId || ''))) throw fail();
          attempt.completed = true; this.save(attempt); return state;
        }
        if (['unknown', 'rejected'].includes(state.status)) throw fail(pendingMessage, state.status);
        if (profile || state.status !== 'pending' || !/^monthly_[a-f0-9]{64}$/.test(state.intentId || '')) throw fail();
        onPending(pendingMessage); await this.wait();
        state = await this.request(sessionToken, '/api/customer/monthly/status', { intentId: state.intentId });
      }
      throw fail();
    } finally { this.busy = false; }
  }
}
let shared;
export function getMonthlyContinuityClient() { shared ||= new MonthlyContinuityClient(); return shared; }
